import { useEffect, useRef } from 'react';
import { AuthLayout, Spinner, Stack, Surface, Text } from '../ui';
import { useNavigate } from '@tanstack/react-router';
import { api, ApiError } from '../lib/api';
import { useWorkspaceId } from '../lib/auth';
import { OAUTH_TOAST_KEY } from './OAuthCallbackPage';

/**
 * HubSpot OAuth redirect landing page (`/oauth/hubspot/callback`, WP-R6). Mirrors the
 * Google mailbox-connect callback exactly: HubSpot redirects the browser here with
 * `?code&state` after the consent screen. This page:
 *   1. reads `code` + `state` (or an `error` HubSpot may send if the user declined);
 *   2. posts them to the API callback, which verifies the signed state (CSRF),
 *      exchanges the code for tokens, and creates the `hubspot` connection (the
 *      tokens NEVER touch the browser);
 *   3. routes back to Connections with a one-shot toast (via the shared toast key).
 */
export function HubspotOAuthCallbackPage() {
  const navigate = useNavigate();
  const workspaceId = useWorkspaceId();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');

    const finish = (toast: string): void => {
      try {
        sessionStorage.setItem(OAUTH_TOAST_KEY, toast);
      } catch {
        /* private mode — the connection still landed; we just skip the toast */
      }
      void navigate({ to: '/app/connections' });
    };

    if (oauthError) {
      finish(`HubSpot connect was cancelled or denied (${oauthError}).`);
      return;
    }
    if (!code || !state) {
      finish('HubSpot connect failed: missing authorization code.');
      return;
    }

    void api
      .completeHubspotConnect(workspaceId, code, state)
      .then((view) => finish(`Connected HubSpot account ${view.externalAccount}.`))
      .catch((err) =>
        finish(err instanceof ApiError ? `HubSpot connect failed: ${err.message}` : 'HubSpot connect failed.'),
      );
  }, [navigate, workspaceId]);

  return (
    <AuthLayout maxWidth="28rem" minHeight="60vh">
      <Surface tone="default" bordered radius="lg" elevation="sm" padding="8">
        <Stack gap="3" align="center">
          <Spinner size="lg" />
          <Text tone="secondary">Finishing HubSpot connection…</Text>
        </Stack>
      </Surface>
    </AuthLayout>
  );
}
